/**
 * Analytics Service
 *
 * Centralized wrapper around PostHog for product analytics, error tracking,
 * and session replay. All code imports from here — never directly from the SDK.
 *
 * No-ops gracefully when env vars are missing (app runs fine without analytics).
 */

import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import type { AnnouncementPayload } from '../types/announcement';
import { isValidAnnouncementPayload } from '../types/announcement';
import { AUTH_NETWORK_ERROR_CODE, isNetworkAuthError, isOfflineFetchError } from '../utils/authErrors';
import { currentFirestoreTransport } from '../utils/firestoreTransport';
import { startMainThreadWatch } from './mainThreadWatch';

let initialized = false;

/**
 * Rewrite offline failures out of error tracking.
 *
 * A dropped connection is not a bug — the auth screen already shows an offline
 * banner with a retry — but it was arriving as an unhandled `$exception` and
 * opening a fresh error-tracking issue every time. Keep it countable as a plain
 * product event instead. Two shapes:
 *
 * - Firebase auth's `auth/network-request-failed` → `auth_network_error`.
 * - A fetch that never reached the server — "Load failed" (Safari), "Failed to
 *   fetch" (Chromium), "NetworkError when attempting to fetch resource"
 *   (Firefox) → `network_error`. These started arriving once console.error
 *   became a captured exception; a cook on a flaky connection logs a dozen of
 *   them in a session, none of which is a bug.
 * - A webfont that took longer than expo-font's 6 s (fontfaceobserver's
 *   "6000ms timeout exceeded") → `font_load_timeout`. The app carries on in
 *   system fonts and the font swaps in when it arrives; `app/_layout.tsx`
 *   swallows the rejection, but PostHog's own listener sees it first.
 *
 * Rewrites rather than re-captures: `before_send` runs synchronously inside
 * capture, and a nested capture would be dropped or recurse.
 */
export function rewriteOfflineExceptions(event: CaptureResult | null): CaptureResult | null {
  if (!event || event.event !== '$exception') return event;

  const list = event.properties?.$exception_list as any[] | undefined;
  if (!Array.isArray(list)) return event;
  const isAuthOffline = list.some(
    (e: any) => isNetworkAuthError(e?.value) || isNetworkAuthError(e?.type)
  );
  const isFetchOffline = !isAuthOffline && list.some((e: any) => isOfflineFetchError(e?.value));
  const fontTimeout = isAuthOffline || isFetchOffline ? null : fontLoadTimeoutMs(list[0]?.value);
  if (!isAuthOffline && !isFetchOffline && fontTimeout == null) return event;

  // Strip every $exception* property so PostHog does not process it as an error.
  const properties = Object.fromEntries(
    Object.entries(event.properties ?? {}).filter(([key]) => !key.startsWith('$exception'))
  );
  const handled = list[0]?.mechanism?.handled ?? null;

  if (isAuthOffline) {
    return {
      ...event,
      event: 'auth_network_error',
      properties: { ...properties, auth_error_code: AUTH_NETWORK_ERROR_CODE, handled },
    };
  }
  if (fontTimeout != null) {
    return {
      ...event,
      event: 'font_load_timeout',
      properties: { ...properties, timeout_ms: fontTimeout, handled },
    };
  }
  return {
    ...event,
    event: 'network_error',
    properties: {
      ...properties,
      network_error_kind: 'fetch',
      network_error_message: String(list[0]?.value ?? '').slice(0, 200),
      handled,
    },
  };
}

// fontfaceobserver's whole message, and nothing else: a timeout of our own
// that happens to say "timeout exceeded" stays an error.
const FONT_LOAD_TIMEOUT_RE = /^(\d+)ms timeout exceeded$/;

/** The timeout in ms when `message` is fontfaceobserver's rejection, else null. */
export function fontLoadTimeoutMs(message: unknown): number | null {
  const match = typeof message === 'string' ? FONT_LOAD_TIMEOUT_RE.exec(message) : null;
  return match ? Number(match[1]) : null;
}

/**
 * A password-reset link's `oobCode` is a one-hour key to the account
 * (`/auth/reset?oobCode=…`). The first line of defence is `public/index.html`,
 * which takes it out of the address bar before posthog-js loads. This is the
 * backstop for anything that still carries one: every string, at any depth
 * (an exception's frames sit five levels down).
 *
 * The cut is targeted (the URL stays readable), then checked against the
 * fully percent-decoded result. If that still reads as a code with a value —
 * a `#` or `;` separator, an encoded key, double encoding, JSON — the whole
 * string goes: a URL parser and a regex never agree on every input, so
 * this fails closed.
 */
const RESET_CODE_PLAIN = /([?&#;]oobCode=)[^&#\s"']+/gi;
const RESET_CODE_ENCODED = /((?:%3F|%26|%23)oobCode%3D)(?:(?!%26|%23)[^&#\s"'])+/gi;
const RESET_CODE_LEFT = /oobcode\s*(?:=|"?\s*:\s*"?)\s*(?!\[redacted\])[^\s&#"'[\]]/i;
const RESET_CODE_KEY = /^oob_?code$/i;
const REDACTED = '[redacted]';

function decodeFully(value: string): string {
  let current = value;
  for (let i = 0; i < 3; i++) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      break;
    }
    if (next === current) break;
    current = next;
  }
  return current;
}

function redactResetCode(value: string): string {
  if (!/oob|%/i.test(value)) return value;
  const cut = value.replace(RESET_CODE_PLAIN, `$1${REDACTED}`).replace(RESET_CODE_ENCODED, `$1${REDACTED}`);
  return RESET_CODE_LEFT.test(cut) || RESET_CODE_LEFT.test(decodeFully(cut)) ? REDACTED : cut;
}

function scrubValue(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return redactResetCode(value);
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, seen));
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [
      k,
      RESET_CODE_KEY.test(k) && typeof v === 'string' ? REDACTED : scrubValue(v, seen),
    ]),
  );
}

/** rrweb's Meta (4) and Custom (5) events: where a recording keeps the page URL. */
const RRWEB_META = 4;
const RRWEB_CUSTOM = 5;

/**
 * A replay snapshot carries the page's DOM, gzipped, which is too big to walk
 * on every frame and holds no URL. The address is in the small Meta and Custom
 * events beside it (`href`). Today the recorder loads lazily and starts after
 * the reset screen has cleaned the address bar; those frames are scrubbed for
 * the day it starts sooner (a bundled recorder, a cached script, a slow tick).
 */
function scrubSnapshot(event: CaptureResult): CaptureResult {
  const data = event.properties?.$snapshot_data;
  if (!Array.isArray(data)) return event;
  const seen = new WeakSet<object>();
  return {
    ...event,
    properties: {
      ...event.properties,
      $snapshot_data: data.map((frame) =>
        frame && (frame.type === RRWEB_META || frame.type === RRWEB_CUSTOM) ? scrubValue(frame, seen) : frame,
      ),
    },
  };
}

export function scrubResetCodes(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  if (event.event === '$snapshot') return scrubSnapshot(event);
  const seen = new WeakSet<object>();
  return {
    ...event,
    properties: scrubValue(event.properties, seen) as CaptureResult['properties'],
    ...(event.$set ? { $set: scrubValue(event.$set, seen) as CaptureResult['$set'] } : {}),
    ...(event.$set_once ? { $set_once: scrubValue(event.$set_once, seen) as CaptureResult['$set_once'] } : {}),
  };
}

/**
 * True when the page is served from a developer machine (localhost, loopback,
 * a .local mDNS name, or a private LAN address such as an Expo dev server on
 * 192.168.x.x).
 *
 * Returns false when there is no browser location to inspect (SSR, tests,
 * native) so those paths keep their existing behaviour.
 */
function isLocalHostname(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;

  // Strip IPv6 brackets: window.location.hostname is "[::1]" for the IPv6 loopback.
  const hostname = (window.location.hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  if (!hostname) return false;

  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return true;
  if (hostname.endsWith('.local')) return true;
  if (hostname === '::1' || hostname === '0.0.0.0') return true;
  if (/^127\./.test(hostname)) return true;

  // Private LAN ranges — Expo serves the web bundle on one of these when you
  // open the dev server from another device on the network.
  if (/^10\./.test(hostname)) return true;
  if (/^192\.168\./.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;

  return false;
}

/**
 * Escape hatch for testing instrumentation locally. Set
 * EXPO_PUBLIC_POSTHOG_ALLOW_LOCALHOST=true in .env.local when you actually
 * want dev events in PostHog; the default is off.
 */
function localCaptureOptIn(): boolean {
  const flag = process.env.EXPO_PUBLIC_POSTHOG_ALLOW_LOCALHOST;
  return flag === 'true' || flag === '1';
}

/**
 * Initialize PostHog. Call once at app startup (fire-and-forget).
 * Safe to call without env vars — silently no-ops.
 *
 * Skips initialization entirely on developer machines: hot-reload artifacts and
 * dev-time typos were opening error-tracking issues that no user ever hit,
 * which buries real production errors in triage noise.
 */
export async function initAnalytics(): Promise<void> {
  const apiKey = process.env.EXPO_PUBLIC_POSTHOG_API_KEY;
  const host = process.env.EXPO_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';

  if (!apiKey) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[Analytics] No EXPO_PUBLIC_POSTHOG_API_KEY — analytics disabled');
    }
    return;
  }

  if (isLocalHostname() && !localCaptureOptIn()) {
    console.log(
      '[Analytics] Local host detected — analytics disabled. ' +
        'Set EXPO_PUBLIC_POSTHOG_ALLOW_LOCALHOST=true in .env.local to capture from dev.'
    );
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: host,
      // We manually fire $pageview events in trackScreen so autocapture doesn't double-count
      capture_pageview: false,
      // Only create person profiles for identified users (reduces costs)
      person_profiles: 'identified_only',
      autocapture: true,
      // Every console.error is an error. Until 2026-10-06 one reached PostHog
      // only as a line in the replay's console pane; a screen that caught a
      // failure and showed a message left no event at all, and "Household …
      // does not exist", five taps in a row, was found by watching the replay.
      // Rate-limited by posthog-js to ten per exception type per session.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      // An offline blip is not a new bug (rewriteOfflineExceptions), and a
      // reset link's code never leaves the device (scrubResetCodes).
      before_send: (event) => scrubResetCodes(rewriteOfflineExceptions(event)),
    });

    posthog.register({
      environment: typeof __DEV__ !== 'undefined' && __DEV__ ? 'development' : 'production',
      // Which Firestore transport this page runs on, so recipe_loaded can be
      // compared before and after long polling went on for WebKit (#791).
      firestore_transport: currentFirestoreTransport(typeof document !== 'undefined'),
    });

    initialized = true;

    // A tap that answers late names itself (#865): slow_interaction and
    // long_task, with the element and the route. PostHog's own INP carries
    // only the number.
    startMainThreadWatch(trackEvent);

    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[Analytics] PostHog initialized');
    }
  } catch (error) {
    console.warn('[Analytics] Failed to initialize PostHog:', error);
  }
}

/**
 * Identify a user (call on login / auth state change).
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, any>
): void {
  if (!initialized) return;
  posthog.identify(userId, properties);
}

/**
 * Reset user identity (call on logout).
 *
 * Only an identified browser is reset. A guest has no Firebase user, so the
 * auth listener reports "no user" on every boot; resetting there handed the
 * guest a new distinct_id per visit. That cut the landing page's CTA click off
 * from the quiz it led to, and made every guest who came back the next day
 * look like a stranger.
 */
export function resetUser(): void {
  if (!initialized) return;
  if (posthog.get_property('$user_state') !== 'identified') return;
  posthog.reset();
}

/** Get current URL — used to enrich events with the current page. */
function getWebUrl(): string | undefined {
  return typeof window !== 'undefined' ? window.location.href : undefined;
}

/**
 * Track a custom event.
 */
export function trackEvent(
  event: string,
  properties?: Record<string, any>
): void {
  if (!initialized) return;
  const url = getWebUrl();
  posthog.capture(event, {
    ...(url ? { $current_url: url } : {}),
    ...properties,
  });
}

/**
 * Track an LLM generation as a PostHog $ai_generation event.
 * User association is automatic (uses the current identified user).
 */
export function trackLLMGeneration(properties: {
  $ai_trace_id: string;
  $ai_span_name: string;
  $ai_model: string;
  $ai_provider: string;
  $ai_input_tokens?: number;
  $ai_output_tokens?: number;
  $ai_latency?: number;
  $ai_is_error?: boolean;
  $ai_error?: string;
}): void {
  if (!initialized) return;
  const distinctId = posthog.get_distinct_id();
  posthog.capture('$ai_generation', {
    $ai_distinct_id: distinctId,
    ...properties,
  });
}

/**
 * Track a screen view. Fires $pageview so PostHog web analytics work correctly.
 */
export function trackScreen(
  screenName: string,
  properties?: Record<string, any>
): void {
  if (!initialized) return;
  const url = getWebUrl();
  posthog.capture('$pageview', {
    ...(url ? { $current_url: url } : {}),
    screen_name: screenName,
    ...properties,
  });
}

/**
 * Track leaving a screen. Fires $pageleave so PostHog can compute
 * session duration and bounce rate accurately.
 */
export function trackScreenLeave(
  screenName: string,
  properties?: Record<string, any>
): void {
  if (!initialized) return;
  const url = getWebUrl();
  posthog.capture('$pageleave', {
    ...(url ? { $current_url: url } : {}),
    screen_name: screenName,
    ...properties,
  });
}

/**
 * Capture an error/exception via PostHog.
 */
export function captureError(
  error: Error,
  context?: Record<string, any>
): void {
  if (!initialized) return;
  try {
    const url = getWebUrl();
    posthog.captureException(error, {
      ...(url ? { $current_url: url } : {}),
      ...context,
    });
  } catch {
    // Never let error tracking crash the app
  }
}

/**
 * Flush pending events. posthog-js auto-flushes on page unload — this is a no-op kept
 * for API compatibility in case callers invoke it before backgrounding.
 */
export async function flushAnalytics(): Promise<void> {
  // posthog-js handles flushing automatically via sendBeacon on page unload
}

export function trackAnnouncementSeen(announcementId: string, flagKey: string): void {
  trackEvent('announcement_seen', { announcement_id: announcementId, flag_key: flagKey });
}

export function trackAnnouncementDismissed(announcementId: string): void {
  trackEvent('announcement_dismissed', { announcement_id: announcementId });
}

export function trackAnnouncementCTAClicked(announcementId: string, ctaRoute: string): void {
  trackEvent('announcement_cta_clicked', { announcement_id: announcementId, cta_route: ctaRoute });
}

/**
 * Returns the first unseen announcement from active PostHog flags.
 * Scans all enabled flags with the "announcement_" prefix.
 * Returns null if PostHog is unavailable, no flags match, or all are dismissed.
 */
export function getFirstUnseenAnnouncement(
  dismissedIds: string[]
): { payload: AnnouncementPayload; flagKey: string } | null {
  if (!initialized) return null;
  try {
    const allFlagKeys: string[] = posthog.featureFlags.getFlags();
    const announcementFlagKeys = allFlagKeys.filter(
      key => key.startsWith('announcement_') && posthog.isFeatureEnabled(key)
    );

    for (const flagKey of announcementFlagKeys) {
      const raw = posthog.getFeatureFlagPayload(flagKey);
      if (isValidAnnouncementPayload(raw) && !dismissedIds.includes(raw.id)) {
        return { payload: raw, flagKey };
      }
    }
  } catch {
    // PostHog unavailable — fail silently
  }
  return null;
}

/**
 * Run callback once PostHog feature flags have been loaded from the server.
 * If flags are already cached, fires immediately. No-ops when uninitialized.
 * Use instead of arbitrary setTimeout — this is the correct async signal.
 */
export function waitForFeatureFlags(callback: () => void): void {
  if (!initialized) return;
  posthog.onFeatureFlags(callback);
}

/**
 * Check whether a PostHog feature flag is enabled for the current user.
 * Returns false when PostHog is not initialized (flag is off by default).
 */
export function isFeatureEnabled(flagKey: string): boolean {
  if (!initialized) return false;
  return posthog.isFeatureEnabled(flagKey) ?? false;
}

/**
 * Return the current PostHog distinct ID and session ID.
 * Used to correlate bug reports with session replays.
 */
export function getAnalyticsContext(): { distinctId: string; sessionId: string } | null {
  if (!initialized) return null;
  try {
    return {
      distinctId: posthog.get_distinct_id(),
      sessionId: posthog.get_session_id() ?? '',
    };
  } catch {
    return null;
  }
}
